// src/admin/hooks/useKycSubmission.ts
// Detail fetch for a single KYC submission, used by /kyc/:id. We run three
// queries in parallel after the submission row lands: the submission itself,
// its owning profile (for OCR compare + reviewer header), and the reviewer
// admin row (if any). Doing this in three queries rather than a PostgREST
// embedded select keeps us independent of the generated-types `Relationships: []`
// — every table on sub-project A ships without FK metadata, so embedded
// `profiles!fk_name(…)` selects cannot be typed without a manual cast, and
// the cast hides schema drift at runtime.
//
// Schema adjustments against the plan:
//   - FK column on kyc_submissions is `user_id`, not `borrower_id`.
//   - OCR data lives in `ocr_result_jsonb`; there is no `ocr_data` column.
//   - Reviewer column is `reviewer_admin_id`; rejection message and
//     resubmit-request message both use `review_notes`.
//   - `nrc_number` is derived from OCR JSON (see nrcFromOcr util).
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { nrcFromOcr } from '@admin/utils/kycStatus';
import type { Database, Json } from '@/lib/database.types';

type KycRow = Database['public']['Tables']['kyc_submissions']['Row'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type AdminRow = Database['public']['Tables']['admin_users']['Row'];

export type KycProfilePick = Pick<
  ProfileRow,
  | 'id'
  | 'display_name'
  | 'full_name'
  | 'phone'
  | 'email'
  | 'date_of_birth'
  | 'gender'
  | 'nrc_number'
>;

export interface KycDetail extends KycRow {
  nrc_number: string | null; // derived from ocr_result_jsonb
  profile: KycProfilePick | null;
  reviewer: Pick<AdminRow, 'id' | 'display_name' | 'email'> | null;
}

export function useKycSubmission(id: string | undefined) {
  return useQuery({
    queryKey: ['kyc', id],
    enabled: !!id,
    queryFn: async (): Promise<KycDetail | null> => {
      if (!id) return null;
      const { data: kyc, error: kycErr } = await supabase
        .from('kyc_submissions')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (kycErr) throw kycErr;
      if (!kyc) return null;

      const [profileRes, reviewerRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, display_name, full_name, phone, email, date_of_birth, gender, nrc_number')
          .eq('id', kyc.user_id)
          .maybeSingle(),
        kyc.reviewer_admin_id
          ? supabase
              .from('admin_users')
              .select('id, display_name, email')
              .eq('id', kyc.reviewer_admin_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (profileRes.error) throw profileRes.error;
      if (reviewerRes.error) throw reviewerRes.error;

      return {
        ...kyc,
        nrc_number: nrcFromOcr(kyc.ocr_result_jsonb as Json | null),
        profile: profileRes.data ?? null,
        reviewer: reviewerRes.data ?? null,
      };
    },
  });
}
